import type { OverflowMenuItem } from '../components/OverflowMenu';

export interface CatalogMenuToggle {
  /** Current state — picks the icon + label (shows "Desactivar" when active). */
  isActive: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

export interface CatalogMenuDelete {
  onDelete: () => void;
  /** Disabled (e.g. tenant viewing a global, admin-owned row it can't delete). */
  disabled?: boolean;
  /** Native tooltip shown when disabled (e.g. "No puedes borrar filas globales"). */
  title?: string;
}

export interface CatalogMenuOpts {
  /** Translator. Admin passes its Spanish-fallback resolver; tenant `useLocale().t`. */
  t: (key: string) => string;
  /** Clone action. Omit to hide the Clonar item entirely (tenant no longer
   *  offers cloning catalog entries). */
  onClone?: () => void;
  /** Admin-only active/inactive toggle. Omit for tenant (uses hard delete instead). */
  toggle?: CatalogMenuToggle;
  /** Tenant hard-delete of an owned private row. Omit for admin (uses `toggle`). */
  remove?: CatalogMenuDelete;
}

/**
 * Build the catalog edit-modal 3-dot menu items: Clonar (only when `onClone` is
 * provided) + optionally Activar/Desactivar (admin soft-delete) OR Borrar
 * (tenant hard-delete of an owned row). Single-sources the menu shared by the
 * four admin `*EditModal` components and the tenant create/edit modals.
 */
export function buildCatalogMenu({ t, onClone, toggle, remove }: CatalogMenuOpts): OverflowMenuItem[] {
  const items: OverflowMenuItem[] = [];
  if (onClone) {
    items.push({ key: 'clone', icon: 'content_copy', label: t('catalog.clone'), onClick: onClone });
  }
  if (toggle) {
    items.push({
      key: 'toggle',
      icon: toggle.isActive ? 'toggle_on' : 'toggle_off',
      label: toggle.isActive ? t('catalog.deactivate') : t('catalog.activate'),
      disabled: toggle.disabled,
      onClick: toggle.onToggle,
    });
  }
  if (remove) {
    items.push({
      key: 'delete',
      icon: 'delete',
      label: t('common.delete'),
      variant: 'danger',
      disabled: remove.disabled,
      title: remove.title,
      onClick: remove.onDelete,
    });
  }
  return items;
}
